import React from 'react';

// Spanish month abbreviations for 'YYYY-MM' labels (avoids depending on a dayjs locale).
const MONTHS_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

// 'YYYY-MM' -> "May 2026".
export function monthLabel(m) {
    if (!m || m.length < 7) return m || '';
    const year = m.slice(0, 4);
    const idx = parseInt(m.slice(5, 7), 10) - 1;
    return `${MONTHS_ES[idx] || m.slice(5, 7)} ${year}`;
}

/**
 * Two-line month column header ("Ene" over "2026") so month columns stay narrow and readable.
 * The full label is kept as a tooltip.
 */
export function monthHeader(m) {
    const label = monthLabel(m);
    const [mon, year] = label.split(' ');
    if (!year) return label;
    return (
        <span className="inline-flex flex-col items-center leading-tight" title={label}>
            <span>{mon}</span>
            <span className="text-[10px] font-medium normal-case tracking-normal text-surface-400">{year}</span>
        </span>
    );
}

/**
 * GitHub-style heat: darker = more usage, on a single scale per table. Returns the cell
 * background + a readable text color. Zero is left blank/transparent. A sqrt curve lifts
 * low-mid values so they read as tinted rather than nearly white. The background is an OPAQUE
 * blend (white → indigo) so the global table row-hover rule can't wash it out from behind; the
 * hover darken is applied on top via CSS (see `.heat-cell` in index.css).
 */
export function heatStyle(value, max) {
    if (!value || value <= 0 || !max) return { bg: 'transparent', color: 'inherit' };
    const t = Math.min(1, value / max);
    const e = Math.sqrt(t);
    const mix = (a, b) => Math.round(a + (b - a) * e); // white(255) -> indigo channel
    return {
        bg: `rgb(${mix(255, 79)}, ${mix(255, 70)}, ${mix(255, 229)})`,
        color: e > 0.5 ? '#ffffff' : '#3730a3'
    };
}

/**
 * An Ant Design column that renders a heat cell: the whole cell is tinted by `get(row)` on the
 * shared `max` scale, with the number centered on top. Zero shows blank.
 */
export function heatCol({ title, keyName, max, get, bold = false, width = 68 }) {
    return {
        title,
        key: keyName || title,
        align: 'center',
        width,
        // Tint the inner `.heat-cell` (not the <td>): the <td> row-hover keeps its own background
        // behind the opaque cell, and CSS darkens `.heat-cell` on hover instead of whitening it.
        onCell: () => ({ style: { padding: 0 } }),
        render: (_, r) => {
            const v = get(r);
            const { bg, color } = heatStyle(v, max);
            return (
                <div className="heat-cell" style={{ background: bg, color, fontWeight: bold ? 700 : v ? 600 : 400 }}>
                    {v ? v : ''}
                </div>
            );
        }
    };
}

// Build month heat columns (+ a Total column) for a set of rows that each have `byMonth`/`total`.
// The month cells share one scale; Total gets its own so both read as "darker = more".
export function monthHeatColumns(rows, months, { includeTotal = true } = {}) {
    let maxMonth = 0;
    let maxTotal = 0;
    for (const r of rows || []) {
        for (const m of months) maxMonth = Math.max(maxMonth, r.byMonth?.[m] || 0);
        maxTotal = Math.max(maxTotal, r.total || 0);
    }
    const cols = months.map((m) => heatCol({ title: monthHeader(m), keyName: m, max: maxMonth, get: (r) => r.byMonth?.[m] || 0 }));
    if (includeTotal) {
        cols.push(heatCol({ title: 'Total', keyName: '__total', max: maxTotal, get: (r) => r.total || 0, bold: true, width: 84 }));
    }
    return cols;
}

/**
 * The overall-usage calendar: month rows × day columns (1–31), GitHub-heatmap style, numbers in
 * sight, with a per-month Total. Built from the `daily` list of { date:'YYYY-MM-DD', count }.
 */
export function UsageCalendar({ daily = [] }) {
    const byMonth = new Map();
    let max = 0;
    for (const d of daily) {
        const m = d.date.slice(0, 7);
        const day = parseInt(d.date.slice(8, 10), 10);
        if (!byMonth.has(m)) byMonth.set(m, { days: {}, total: 0 });
        const e = byMonth.get(m);
        e.days[day] = (e.days[day] || 0) + d.count;
        e.total += d.count;
        if (e.days[day] > max) max = e.days[day];
    }
    const months = [...byMonth.keys()].sort();
    const dayNums = Array.from({ length: 31 }, (_, i) => i + 1);

    if (!months.length) {
        return <div className="py-6 text-center text-sm text-surface-400">Sin actividad en el rango seleccionado.</div>;
    }

    // Day cells have no fixed width — with table-layout:fixed + width:100% the 31 columns share the
    // full card width equally (taller cells are fine). A min-height keeps them from getting squat.
    const cellBase = {
        height: 34,
        textAlign: 'center',
        verticalAlign: 'middle',
        borderRadius: 6,
        fontSize: 12,
        fontVariantNumeric: 'tabular-nums'
    };
    const th = 'py-0.5 text-center text-[11px] font-semibold text-surface-400 tabular-nums';

    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]" style={{ tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 3 }}>
                <thead>
                    <tr>
                        <th className={`${th} !text-left pr-2`} style={{ width: 96 }}>
                            Mes
                        </th>
                        {dayNums.map((d) => (
                            <th key={d} className={th}>
                                {d}
                            </th>
                        ))}
                        <th className={`${th} pl-2`} style={{ width: 60 }}>
                            Total
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {months.map((m) => {
                        const e = byMonth.get(m);
                        return (
                            <tr key={m}>
                                <td className="truncate whitespace-nowrap pr-2 text-xs font-semibold text-surface-700">{monthLabel(m)}</td>
                                {dayNums.map((d) => {
                                    const v = e.days[d] || 0;
                                    const { bg, color } = heatStyle(v, max);
                                    return (
                                        <td
                                            key={d}
                                            title={v ? `${d} ${monthLabel(m)}: ${v}` : undefined}
                                            style={{ ...cellBase, background: v ? bg : '#f7f8fa', color, fontWeight: v ? 600 : 400 }}>
                                            {v || ''}
                                        </td>
                                    );
                                })}
                                <td className="pl-2.5 text-right text-[13px] font-bold text-surface-900 tabular-nums">{e.total}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
